/**
 * Persists an import as first-class records: one `ImportedDataset` (the parsed rows + derived widgets)
 * and one `CustomTile` per chart/table widget, wired to read from the dataset via the `File Import`
 * data source. Once saved, those tiles are ordinary dashboard tiles — they can be placed on dashboards
 * and re-render from the stored data with no dependence on the import UI.
 *
 * `topology` widgets are stored in the dataset's `widgets` (so the assessment report can draw them with
 * the isometric renderer) but are intentionally NOT turned into `CustomTile`s: the dashboard tile system
 * has no topology chart type, so a persisted topology tile could not render there.
 */
import { fetchItem, fetchItems, postItem } from '../queries';
import { WidgetSpec } from './presets/types';
import { FILE_IMPORT_SOURCE } from './fileImportSource';

export interface PersistWidgetsInput {
  datasetName: string;
  sourceFileName?: string;
  sourceMeta?: any;
  preset?: string;
  columns: string[];
  rows: Record<string, any>[];
  widgets: WidgetSpec[];
  /**
   * Loose back-link to the assessment response set this import belongs to, kept for back-compat.
   * Prefer the generic `owner` below, which lets a dataset attach to any owner (a campaign delivery,
   * a response set, ...) without a per-owner column.
   */
  responseSetId?: number | null;
  /**
   * Generic owner of the dataset. `type` is a stable slug ('assessment_response_set',
   * 'campaign_delivery', ...) and `id` is that owner's PK as a string. When the owner is a response
   * set, `responseSetId` is also set from it below so the legacy reader keeps working.
   */
  owner?: { type: string; id: string | number } | null;
  updatedBy?: string | null;
}

export interface PersistedTileRef {
  widgetId: string;
  tileId: number;
  chartType: string;
}

export interface PersistWidgetsResult {
  datasetId: number;
  tiles: PersistedTileRef[];
}

/** A widget whose chartType the dashboard tile renderer cannot draw is stored but not persisted as a tile. */
const isTileable = (spec: WidgetSpec): boolean => spec.chartType !== 'topology';

/** CustomTile.name is unique — namespace each generated tile by dataset id so imports never collide. */
const tileName = (spec: WidgetSpec, datasetId: number): string =>
  `${spec.title} (import ${datasetId})`;

export async function persistWidgets(input: PersistWidgetsInput): Promise<PersistWidgetsResult> {
  // Derive the generic owner from either the explicit `owner` or the legacy `responseSetId`, and
  // keep `response_set_id` populated whenever the owner is a response set so old readers still work.
  const owner =
    input.owner ??
    (input.responseSetId != null
      ? { type: 'assessment_response_set', id: input.responseSetId }
      : null);
  const responseSetId =
    input.responseSetId ??
    (owner?.type === 'assessment_response_set' ? Number(owner.id) : null);

  const dataset: any = await postItem('db/imported-dataset', {
    name: input.datasetName,
    source_file_name: input.sourceFileName ?? null,
    source_meta: input.sourceMeta ?? {},
    preset: input.preset ?? null,
    columns: input.columns,
    rows: input.rows,
    widgets: input.widgets,
    response_set_id: responseSetId,
    owner_type: owner?.type ?? null,
    owner_id: owner != null ? String(owner.id) : null,
    updated_by: input.updatedBy ?? null,
    metadata: {},
  });

  const datasetId: number = dataset?.id;
  const tiles: PersistedTileRef[] = [];

  for (const spec of input.widgets) {
    if (!isTileable(spec)) continue;

    const created: any = await postItem('db/custom-tile', {
      name: tileName(spec, datasetId),
      chart_type: spec.chartType,
      queries: [
        {
          dataSource: FILE_IMPORT_SOURCE,
          model: 'imported-dataset',
          function: 'get',
          path: `db/imported-dataset/${datasetId}`,
          method: 'GET',
          importedDatasetId: datasetId,
          widgetId: spec.id,
        },
      ],
      transformations: [],
      settings: spec.settings || {},
      category: null,
      updated_by: input.updatedBy ?? null,
    });

    if (created?.id != null) {
      tiles.push({ widgetId: spec.id, tileId: created.id, chartType: spec.chartType });
    }
  }

  return { datasetId, tiles };
}

/** Load a persisted dataset (rows + widgets) by id — used when re-opening a saved import. */
export async function loadImportedDataset(datasetId: number): Promise<any> {
  return fetchItem(`db/imported-dataset/${datasetId}`);
}

/**
 * Load every persisted dataset attached to one owner (a campaign delivery, a response set, ...),
 * newest first. `loadImportedDataset` reads a single dataset by id; this is its owner-scoped
 * counterpart, needed to re-read the datasets/widgets a caller persisted with an `owner` (e.g. all of
 * a campaign delivery's saved chart datasets) without already knowing their ids. Returns `[]` on miss.
 */
export async function loadDatasetsByOwner(
  ownerType: string,
  ownerId: string | number
): Promise<any[]> {
  const rows = await fetchItems('db/imported-dataset', {
    owner_type: ownerType,
    owner_id: String(ownerId),
  });
  return Array.isArray(rows) ? rows : [];
}
